import { ActionFunction, replace } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  ArchiveOutboundCallsDocument,
  ArchiveOutboundCallsMutation,
  ArchiveOutboundCallsMutationVariables,
  CancelOutboundCallDocument,
  CancelOutboundCallMutation,
  CancelOutboundCallMutationVariables,
  CreateOutboundCallDocument,
  CreateOutboundCallMutation,
  CreateOutboundCallMutationVariables,
  DialOutboundCallDocument,
  DialOutboundCallMutation,
  DialOutboundCallMutationVariables,
  UpdateCallsDocument,
  UpdateCallsMutation,
  UpdateCallsMutationVariables,
  UpdateOutboundCallDocument,
  UpdateOutboundCallMutation,
  UpdateOutboundCallMutationVariables
} from '~/api/graphql';
import { OutboundCallDraft, buildOutboundCallMutationInput } from '~/api/data/calls/input';

export const archiveCalls = (variables: UpdateCallsMutationVariables) =>
  mutateData<UpdateCallsMutation, UpdateCallsMutationVariables>(client, {
    mutation: UpdateCallsDocument,
    variables
  });

export const markCallsAsSeen = (variables: UpdateCallsMutationVariables) =>
  mutateData<UpdateCallsMutation, UpdateCallsMutationVariables>(client, {
    mutation: UpdateCallsDocument,
    variables
  });

const OUTBOUND_CALL_LIST_QUERIES = ['SearchOutboundCalls', 'GetOutboundCallsRefreshCandidates'];

export const createOutboundCall = (variables: CreateOutboundCallMutationVariables) =>
  mutateData<CreateOutboundCallMutation, CreateOutboundCallMutationVariables>(client, {
    mutation: CreateOutboundCallDocument,
    variables,
    // Refetch by operation name so the mounted list reloads with its own
    // filter/sort variables (searchOutboundCalls is cached per filter+sort).
    refetchQueries: OUTBOUND_CALL_LIST_QUERIES,
    awaitRefetchQueries: true
  });

export const updateOutboundCall = (variables: UpdateOutboundCallMutationVariables) =>
  mutateData<UpdateOutboundCallMutation, UpdateOutboundCallMutationVariables>(client, {
    mutation: UpdateOutboundCallDocument,
    variables,
    refetchQueries: OUTBOUND_CALL_LIST_QUERIES,
    awaitRefetchQueries: true
  });

export const dialOutboundCall = (variables: DialOutboundCallMutationVariables) =>
  mutateData<DialOutboundCallMutation, DialOutboundCallMutationVariables>(client, {
    mutation: DialOutboundCallDocument,
    variables,
    refetchQueries: OUTBOUND_CALL_LIST_QUERIES
  });

export const cancelOutboundCall = (variables: CancelOutboundCallMutationVariables) =>
  mutateData<CancelOutboundCallMutation, CancelOutboundCallMutationVariables>(client, {
    mutation: CancelOutboundCallDocument,
    variables,
    refetchQueries: OUTBOUND_CALL_LIST_QUERIES
  });

export const cancelOutboundCalls = async ({ outboundCallIds }: { outboundCallIds: string[] }) => {
  const results = await Promise.all(
    (outboundCallIds ?? []).map(async (id) => {
      try {
        const result = await client.mutate<
          CancelOutboundCallMutation,
          CancelOutboundCallMutationVariables
        >({
          mutation: CancelOutboundCallDocument,
          variables: { id }
        });
        if (result.errors?.length) {
          return { id, ok: false as const };
        }
        return { id, ok: true as const };
      } catch {
        return { id, ok: false as const };
      }
    })
  );
  return { failedIds: results.filter((r) => !r.ok).map((r) => r.id) };
};

export const archiveOutboundCalls = async ({ outboundCallIds }: { outboundCallIds: string[] }) => {
  try {
    await client.mutate<ArchiveOutboundCallsMutation, ArchiveOutboundCallsMutationVariables>({
      mutation: ArchiveOutboundCallsDocument,
      variables: { ids: outboundCallIds ?? [] }
    });
    return { failedIds: [] as string[] };
  } catch {
    return { failedIds: outboundCallIds ?? [] };
  }
};

export const submitArchiveCalls: ActionFunction = async ({ request }) =>
  submitMutation(
    archiveCalls({ callIds: (await request.json()).callIds, input: { archive: true } })
  );

export const submitMarkCallsAsSeen: ActionFunction = async ({ request }) => {
  const body = await request.json();
  return submitMutation(
    markCallsAsSeen({ callIds: body.callIds, input: { markSeen: body.markSeen ?? true } })
  );
};

export const submitArchiveOutboundCalls: ActionFunction = async ({ request }) =>
  archiveOutboundCalls(await request.json());

export const submitCancelOutboundCalls: ActionFunction = async ({ request }) =>
  cancelOutboundCalls(await request.json());

export const submitCreateOutboundCall: ActionFunction = async ({ request }) => {
  const body = (await request.json()) as OutboundCallDraft;
  return submitMutation(
    createOutboundCall({ input: buildOutboundCallMutationInput(body) }),
    async () => replace('/app/calls/outbound')
  );
};

export const submitUpdateOutboundCall: ActionFunction = async ({ params, request }) => {
  const body = (await request.json()) as OutboundCallDraft;
  return submitMutation(
    updateOutboundCall({
      id: params.id || '',
      input: buildOutboundCallMutationInput(body)
    }),
    async () => replace(`/app/calls/outbound/${params.id}`)
  );
};

export const submitDialOutboundCall: ActionFunction = async ({ params }) =>
  submitMutation(dialOutboundCall({ id: params.id || '' }));

export const submitCancelOutboundCall: ActionFunction = async ({ params }) =>
  submitMutation(cancelOutboundCall({ id: params.id || '' }));
